import React, { useEffect, useState } from "react";
import styles from "./OurProgrammes.module.css";
import Link from "next/link";
import { ChevronRight, ChevronLeft } from "lucide-react";

type ProgramType = {
  id: number;
  program_name: string;
  nqf_level: string;
  study_mode: string;
  duration: string;
  program_level: string;
  program_detail_url: string;
};

type SchoolType = {
  id: number;
  schools: string;
  programs: ProgramType[];
};

type OurProgrammesProps = {
  data?: {
    id: number;
    title: string;
    program_cards: any[];
    program_sections: SchoolType[];
  };
};

const OurProgrammes: React.FC<OurProgrammesProps> = ({ data }) => {
  const [selectedSchool, setSelectedSchool] = useState<number | "All">("All");
  const [programType, setProgramType] = useState<
    "AllCourses" | "PostgraduateProgrammes" | "UndergraduateProgrammes"
  >("AllCourses");
  const [pageIndex, setPageIndex] = useState<number>(0);
  const [isMobile, setIsMobile] = useState(false);
  const itemsPerPage = 11;

  useEffect(() => {
    if (typeof window !== "undefined") {
      const handleResize = () => {
        setIsMobile(window.innerWidth <= 767);
      };
      handleResize();
      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    }
  }, []);

  if (!data || !data.program_sections) {
    return <div>Loading...</div>;
  }

  const schoolNames = data?.program_sections?.map((section: SchoolType) => ({
    id: section?.id,
    name: section?.schools,
  }));

  const handleSchoolChange = (schoolId: number | "All") => {
    setSelectedSchool(schoolId);
    setProgramType("AllCourses");
    setPageIndex(0);
  };

  let programmes: ProgramType[] = [];

  if (selectedSchool === "All") {
    programmes = data?.program_sections?.flatMap(
      (section: SchoolType) => section?.programs
    );
  } else {
    const selectedSchoolData = data?.program_sections?.find(
      (section: SchoolType) => section?.id === selectedSchool
    );
    programmes = selectedSchoolData ? selectedSchoolData?.programs : [];
  }

  const paginatedProgrammes = programmes?.slice(
    pageIndex * itemsPerPage,
    (pageIndex + 1) * itemsPerPage
  );

  const filteredProgrammes =
    programType === "UndergraduateProgrammes"
      ? paginatedProgrammes?.filter(
          (program: ProgramType) => program?.program_level === "UG"
        )
      : programType === "PostgraduateProgrammes"
      ? paginatedProgrammes?.filter(
          (program: ProgramType) => program?.program_level === "PG"
        )
      : paginatedProgrammes;

  return (
    <div className={styles.ourProgrammesTable}>
      <div className="container">
        <div className={styles.topSection}>
          <h1 className={styles.title}>{data.title}</h1>
          <div className={styles.buttonGroup}>
            <select
              className={`${styles.dropdown} ${
                programType === "AllCourses" ? styles.activeButton : ""
              }`}
              value={selectedSchool}
              onChange={(e) =>
                handleSchoolChange(
                  e.target.value === "All" ? "All" : parseInt(e.target.value)
                )
              }
            >
              <option className={styles.dropdownOption} value="All">
                Schools
              </option>
              {schoolNames.map((school) => (
                <option
                  className={styles.dropdownOption}
                  key={school.id}
                  value={school.id}
                >
                  {school.name.replace(/-/g, " ")}
                </option>
              ))}
            </select>

            <button
              className={`${styles.button} ${
                programType === "UndergraduateProgrammes"
                  ? styles.activeButton
                  : ""
              }`}
              onClick={() => setProgramType("UndergraduateProgrammes")}
            >
              {isMobile ? "UG" : "Undergraduate"}
            </button>
            <button
              className={`${styles.button} ${
                programType === "PostgraduateProgrammes"
                  ? styles.activeButton
                  : ""
              }`}
              onClick={() => setProgramType("PostgraduateProgrammes")}
            >
              {isMobile ? "PG" : "Postgraduate"}
            </button>
          </div>
        </div>

        <table className={styles.programmeTable}>
          <thead>
            <tr>
              <th>Programme Name</th>
              <th>NQF Level</th>
              <th>Study Mode</th>
              <th>Duration</th>
            </tr>
          </thead>
          <tbody>
            {filteredProgrammes?.map((programme) => (
              <tr key={programme.id}>
                <td>
                  <Link
                    href={programme?.program_detail_url}
                    title={programme?.program_name}
                  >
                    {programme?.program_name}
                  </Link>
                </td>
                <td>{programme?.nqf_level}</td>
                <td>{programme?.study_mode}</td>
                <td>{programme?.duration}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className={styles.programmeTableMobile}>
          <ul>
            {filteredProgrammes?.map((programme) => (
              <li key={programme?.id}>
                {programme?.program_name}
                <Link
                  href={programme?.program_detail_url}
                  title={programme?.program_name}
                >
                  <ChevronRight />
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {programmes?.length > itemsPerPage && (
          <div className={styles.pagination}>
            <button
              className={styles.arrowButton}
              onClick={() => setPageIndex((prev) => Math?.max(prev - 1, 0))}
              disabled={pageIndex === 0}
            >
              <ChevronLeft />
            </button>

            {Array?.from(
              { length: Math?.ceil(programmes?.length / itemsPerPage) },
              (_, pageNum) => (
                <button
                  key={pageNum}
                  className={
                    pageIndex === pageNum
                      ? styles.activePage
                      : styles.pageButton
                  }
                  onClick={() => setPageIndex(pageNum)}
                >
                  {pageNum + 1}
                </button>
              )
            )}

            <button
              className={styles.arrowButton}
              onClick={() =>
                setPageIndex((prev) =>
                  Math.min(
                    prev + 1,
                    Math?.ceil(programmes?.length / itemsPerPage) - 1
                  )
                )
              }
              disabled={
                pageIndex === Math.ceil(programmes?.length / itemsPerPage) - 1
              }
            >
              <ChevronRight />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default OurProgrammes;
